import React from 'react';
import hoistNonReactMethods from 'hoist-non-react-methods';

import { st, classes } from './Focusable.st.css';
import {
  getDisplayName,
  isRefableComponent,
  isStatelessComponent,
} from '../hocUtils';

type SubscribeCb = () => void;
type InputMethod = 'mouse' | 'keyboard';
type FocusableOptions = {
  isFocusWithMouse?: boolean;
};

interface FocusOptions {
  focusVisible?: boolean;
}

export type Triggers = {
  blur: () => void;
  focus: (options?: FocusOptions) => void;
};

export type WithFocusableProps<Element extends HTMLElement = HTMLElement> = {
  disabled?: boolean;
  onFocus?: (event: FocusEvent, triggers: Triggers) => void;
  onBlur?: (event: FocusEvent, triggers: Triggers) => void;
  ref?: React.RefObject<Element>;
  className?: string;
};

export type InjectedFocusableProps<P extends object, E = HTMLElement> = P & {
  focusableOnFocus?: React.FocusEventHandler<E>;
  focusableOnBlur?: React.FocusEventHandler<E>;
  ref?: React.RefObject<E>;
  className?: string;
};

/**
 * Singleton for managing current input method (keyboard or mouse).
 */
const inputMethod = new (class {
  // Default is keyboard in case an element is focused programmatically.
  method: InputMethod = 'keyboard';
  subscribers: Map<any, SubscribeCb> = new Map();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', () => this.setMethod('mouse'));
      window.addEventListener('keydown', () => this.setMethod('keyboard'));
      // We need to listen on keyUp, in case a TAB is made from the browser's address-bar,
      // so the keyDown is not fired, only the keyUp.
      window.addEventListener('keyup', () => this.setMethod('keyboard'));
    }
  }

  subscribe = (target: any, callback: SubscribeCb) =>
    this.subscribers.set(target, callback);

  unsubscribe = (target: any) => this.subscribers.delete(target);

  /**
   * Is the current input method `keyboard`. if `false` is means it is `mouse`
   */
  isKeyboard = () => this.method === 'keyboard';

  setMethod(method: InputMethod) {
    if (method !== this.method) {
      this.method = method;
      this.subscribers.forEach(f => f());
    }
  }
})();

/*
 * TODO: Consider adding 'disabled' state to this HOC, since:
 * - When component is focused and then it becomes disabled, then the focus needs to be blurred.
 *
 * TODO: Consider using [Recompose](https://github.com/acdlite/recompose/tree/master/src/packages/recompose) to do:
 *  - the static hoisting
 *  - set displayName
 */
export function withFocusable<
  T extends object,
  E extends HTMLElement = HTMLElement,
>(
  Component: React.ComponentType<T>,
  options: FocusableOptions = {},
): React.ComponentType<T & WithFocusableProps<E>> {
  interface FocusableState {
    focus: boolean;
    focusVisible: boolean;
  }

  class Focusable extends React.Component<
    T & WithFocusableProps<E>,
    FocusableState
  > {
    static displayName = getDisplayName(Component);
    static defaultProps = Component.defaultProps;

    wrappedComponentRef: any = null;

    state = {
      focus: false,
      focusVisible: false,
    };

    componentWillUnmount() {
      inputMethod.unsubscribe(this);
    }

    componentDidUpdate(prevProps: WithFocusableProps) {
      /*
        in case when button was focused and then become disabled,
        we need to trigger blur logic and remove all listers, as disabled button
        do not trigger onFocus and onBlur events
      */
      const isFocused = this.state.focus || this.state.focusVisible;
      const isBecomeDisabled = !prevProps.disabled && this.props.disabled;
      if (isFocused && isBecomeDisabled) {
        this.onBlur({} as FocusEvent);
      }
    }

    focus = (options?: FocusOptions) => {
      if (this.wrappedComponentRef?.focus) {
        this.wrappedComponentRef.focus();

        // If focusVisible option is explicitly provided, update the state accordingly
        if (options && options.focusVisible !== undefined) {
          this.setState({
            focus: true,
            focusVisible: options.focusVisible,
          });
        }
      }
    };

    setWrappedComponentRef = (ref: React.RefObject<E>) => {
      this.wrappedComponentRef = ref;
    };

    markAsFocused = () => {
      this.setState({
        focus: true,
        focusVisible: options.isFocusWithMouse || inputMethod.isKeyboard(),
      });
      inputMethod.subscribe(this, () => {
        if (options.isFocusWithMouse || inputMethod.isKeyboard()) {
          this.setState({ focusVisible: true });
        }
      });
    };

    markAsBlurred = () => {
      inputMethod.unsubscribe(this);
      this.setState({ focus: false, focusVisible: false });
    };

    onFocus = (event: FocusEvent) => {
      const { onFocus } = this.props;

      if (onFocus) {
        onFocus(event, {
          blur: this.markAsBlurred,
          focus: this.markAsFocused,
        });
      }

      this.markAsFocused();
    };

    onBlur = (event: FocusEvent) => {
      const { onBlur } = this.props;

      if (onBlur) {
        onBlur(event, { blur: this.markAsBlurred, focus: this.markAsFocused });
      }

      this.markAsBlurred();
    };

    render() {
      const reference = isRefableComponent(Component)
        ? this.setWrappedComponentRef
        : undefined;

      return (
        <Component
          {...this.props}
          ref={reference}
          focusableOnFocus={this.onFocus}
          focusableOnBlur={this.onBlur}
          className={st(
            classes.root,
            {
              focus: this.state.focus,
              'focus-visible': this.state.focusVisible,
            },
            this.props.className,
          )}
        />
      );
    }
  }

  return isStatelessComponent(Component)
    ? Focusable
    : hoistNonReactMethods(Focusable, Component, {
        delegateTo: (c: { wrappedComponentRef: any }) => c.wrappedComponentRef,
        hoistStatics: true,
      });
}
